import { A3Camera } from '@imaaagery/a3camera';
import type { CameraError, FaceLandmarksDetectionResults, FaceLandmarksDetectionStatus } from '@imaaagery/a3camera';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { getAnalysis } from '../api/demky/analysis';
import { AnalysisError } from '../type';
import badIcon from '../assets/icons/CameraClose.svg';
import okIcon from '../assets/icons/CameraOK.svg';
import EnlargedOvalImg from '../assets/icons/Enlarged_Oval.png';
import OvalImg from '../assets/icons/Oval.png';
import resetIcon from '../assets/icons/Reset.svg';
import tickIcon from '../assets/icons/Tick.svg';
import debugFace from '../assets/Images/hyperreal/CaucasianFace.jpg';
import * as actionTypes from '../constantes/actionTypes';
import {
	BottomContainer,
	CameraIcon,
	CameraPanel,
	CameraWarningText,
	CheckingButton,
	CheckingContainer,
	CheckingIcon,
	CheckingRoundButton,
	CheckingText,
	CheckingVerticalCenter,
	CountDownNumberBis,
	EyeText,
	Flash,
	Oval,
	OvalContainer,
	UserImg,
	WarningContainer,
	Wave,
	WaveContainer,
	WhiteText,
	WarningErrorBackground,
	WarningErrorContainer,
} from '../styles/cameraStyle';
import { CliniqueState, StepView } from '../type';
import { sendAnalytics, translateError } from '../constantes/utilitaries';
import { DETECTION_CONSTRAINTS_CENTER, PHOTO_SETTINGS, VIDEO_CONSTRAINTS, WARNINGS } from './cameraConstants';
import {
	isLightModelResultOk,
	LIGHT_MODEL_INTERVAL_MS,
	LIGHT_MODEL_OK_VALIDITY_MS,
	loadLightModel,
	type LightModelResult,
	runLightModel,
} from './lightDetection';
import { trackPlausibleEvent } from '../services/analytics';

const areStatusesEqual = (previous: FaceLandmarksDetectionStatus | undefined, next: FaceLandmarksDetectionStatus) => {
	if (previous === next) return true;
	if (!previous) return false;

	const previousKeys = Object.keys(previous);
	const nextKeys = Object.keys(next);
	if (previousKeys.length !== nextKeys.length) return false;

	return previousKeys.every((key) => previous[key as keyof FaceLandmarksDetectionStatus] === next[key as keyof FaceLandmarksDetectionStatus]);
};

const isLocalhostDebugMode = () => {
	return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
};

const isUserWellPlaced = (status: FaceLandmarksDetectionStatus) => {
	return (
		status.loading !== true &&
		status.noFace !== true &&
		status.badDistanceFar !== true &&
		status.badDistanceClose !== true &&
		status.badPositionBottom !== true &&
		status.badPositionLeft !== true &&
		status.badPositionRight !== true &&
		status.badPositionTop !== true &&
		status.badOrientation !== true &&
		status.badBrightness !== true
	);
};

const SHOW_LIGHT_MODEL_PREVIEW = false;

type NewCameraProps = {
	isShadematch?: boolean;
};

const NewCamera = ({ isShadematch = true }: NewCameraProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const userImg = useSelector((state: CliniqueState) => state.session.imageUser);
	const storeErrors = useSelector((state: CliniqueState) => state.session.error);
	const sessionID = useSelector((state: CliniqueState) => state.session.sessionID);
	const [detectionStatus, setDetectionStatus] = useState<FaceLandmarksDetectionStatus>({ loading: true });
	const [countdownValue, setCountdownValue] = useState<number | null>(null);
	const [isPhotoCaptured, setIsPhotoCaptured] = useState(false);
	const [isFlashing, setIsFlashing] = useState(false);
	const [isWavesDisplayed, displayWaves] = useState(true);
	const [isSkinDiagLoading, setSkinDiagLoading] = useState(false);
	const [isSkinDiagValidated, setSkinDiagValidated] = useState(false);
	const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const countdownRunningRef = useRef(false);
	const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lastOvalClickTimeRef = useRef(0);
	const cameraVideoRef = useRef<HTMLVideoElement>(null);
	const lightModelPreviewRef = useRef<HTMLCanvasElement>(null);
	const detectionStatusRef = useRef<FaceLandmarksDetectionStatus>({ loading: true });
	const lightModelSessionRef = useRef<Awaited<ReturnType<typeof loadLightModel>> | null>(null);
	const lightModelRunningRef = useRef(false);
	const lastLightModelOkAtRef = useRef(0);
	const [lightModelResult, setLightModelResult] = useState<LightModelResult | null>(null);
	const isLightReady = !isShadematch || isLightModelResultOk(lightModelResult);
	const isCameraReady = detectionStatus.good === true && isLightReady;

	const clearCountdownTimer = useCallback(() => {
		if (countdownTimerRef.current) {
			clearInterval(countdownTimerRef.current);
			countdownTimerRef.current = null;
		}
		countdownRunningRef.current = false;
	}, []);

	const stopCountdown = useCallback(() => {
		clearCountdownTimer();
		setCountdownValue(null);
	}, [clearCountdownTimer]);

	const startCountdown = useCallback(() => {
		if (countdownRunningRef.current) return;

		clearCountdownTimer();
		countdownRunningRef.current = true;
		let currentCount = 3;
		setCountdownValue(currentCount);

		countdownTimerRef.current = setInterval(() => {
			currentCount -= 1;
			if (currentCount > 0) {
				setCountdownValue(currentCount);
			} else {
				stopCountdown();
			}
		}, 1000);
	}, [clearCountdownTimer, stopCountdown]);

	const handleStatusChange = useCallback((status: FaceLandmarksDetectionStatus) => {
		detectionStatusRef.current = status;
		setDetectionStatus((previous) => (areStatusesEqual(previous, status) ? previous : status));
	}, []);

	useEffect(() => {
		if (!isShadematch) return;

		if (!isUserWellPlaced(detectionStatus)) {
			setLightModelResult(null);
		}
	}, [detectionStatus, isShadematch]);

	useEffect(() => {
		if (!isShadematch) return;

		let isActive = true;

		loadLightModel()
			.then((session) => {
				if (isActive) {
					lightModelSessionRef.current = session;
				}
			})
			.catch((error) => {
				console.error('[Shadematch] light model failed to load', error);
			});

		return () => {
			isActive = false;
			lightModelSessionRef.current = null;
		};
	}, [isShadematch]);

	useEffect(() => {
		if (!isShadematch) return;

		const interval = window.setInterval(async () => {
			const session = lightModelSessionRef.current;
			const video = cameraVideoRef.current;

			if (
				!session ||
				!video ||
				video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
				lightModelRunningRef.current ||
				!isUserWellPlaced(detectionStatusRef.current)
			) {
				return;
			}

			lightModelRunningRef.current = true;

			try {
				const result = await runLightModel(session, video, lightModelPreviewRef.current);
				if (!result) {
					return;
				}

				if (isLightModelResultOk(result)) {
					lastLightModelOkAtRef.current = performance.now();
				}
				setLightModelResult(result);
			} catch (error) {
				console.error('[Shadematch] light model inference failed', error);
			} finally {
				lightModelRunningRef.current = false;
			}
		}, LIGHT_MODEL_INTERVAL_MS);

		return () => window.clearInterval(interval);
	}, [isShadematch]);

	useEffect(() => {
		if (isShadematch || !isPhotoCaptured || !isSkinDiagLoading || storeErrors.length === 0) return;

		if (storeErrors.includes(AnalysisError.no_error)) {
			setSkinDiagLoading(false);
			setSkinDiagValidated(true);
			return;
		}

		displayWaves(false);
		sendAnalytics('take photo validation', `error taking | ${storeErrors[0]}`, sessionID);
		trackPlausibleEvent('sd_photo_failed', { props: { code: storeErrors[0] } });

		const resetTimer = window.setTimeout(() => {
			dispatch({ type: actionTypes.REMOVE_ANALYSIS_WARNINGS });
			setIsPhotoCaptured(false);
			setSkinDiagLoading(false);
			setSkinDiagValidated(false);
			displayWaves(true);
			setDetectionStatus({ loading: true });
		}, 3000);

		return () => window.clearTimeout(resetTimer);
	}, [dispatch, isPhotoCaptured, isShadematch, isSkinDiagLoading, sessionID, storeErrors]);

	const handleLightConstraint = useCallback(() => {
		return performance.now() - lastLightModelOkAtRef.current <= LIGHT_MODEL_OK_VALIDITY_MS;
	}, []);

	const startSkinDiagAnalysis = useCallback((image: string) => {
		setSkinDiagLoading(true);
		setSkinDiagValidated(false);
		getAnalysis(image, dispatch);
	}, [dispatch]);

	const displayCapturedPhoto = useCallback((image: string, predictions?: FaceLandmarksDetectionResults['predictions']) => {
		if (flashTimerRef.current) {
			clearTimeout(flashTimerRef.current);
			flashTimerRef.current = null;
		}

		setIsFlashing(true);
		flashTimerRef.current = setTimeout(() => {
			setIsFlashing(false);
			flashTimerRef.current = null;
		}, 300);
		dispatch({ type: actionTypes.ANALYSE_PHOTO, value: image });
		dispatch({ type: actionTypes.SET_PREDICTIONS, value: predictions ?? null });
		if (isShadematch) {
			dispatch({ type: actionTypes.NO_WARNING });
		}
		setIsPhotoCaptured(true);
		displayWaves(false);
		stopCountdown();
		trackPlausibleEvent(isShadematch ? 'sm_photo_captured' : 'sd_photo_captured');
		trackPlausibleEvent(isShadematch ? 'sm_photo_succeeded' : 'sd_photo_succeeded');

		if (!isShadematch) {
			sendAnalytics('photo upload or take', 'take photo', sessionID);
			startSkinDiagAnalysis(image);
		}
	}, [dispatch, isShadematch, sessionID, startSkinDiagAnalysis, stopCountdown]);

	const handlePhotoTaken = useCallback((result: FaceLandmarksDetectionResults) => {
		if (!result.image) {
			trackPlausibleEvent(isShadematch ? 'sm_photo_failed' : 'sd_photo_failed', {
				props: { reason: 'missing_image' },
			});
			return;
		}

		displayCapturedPhoto(result.image, result.predictions);
	}, [displayCapturedPhoto, isShadematch]);

	const handleCameraError = useCallback((error: CameraError) => {
		if (error === 'access-denied') {
			trackPlausibleEvent(isShadematch ? 'sm_camera_permission_denied' : 'sd_camera_permission_denied', {
				props: { camera: 'face' },
			});
			return;
		}

		trackPlausibleEvent(isShadematch ? 'sm_photo_failed' : 'sd_photo_failed', {
			props: { camera: 'face', reason: error },
		});
	}, [isShadematch]);

	const handleDebugOvalClick = useCallback(() => {
		if (!isLocalhostDebugMode()) return;

		const now = Date.now();
		const isDoubleClick = now - lastOvalClickTimeRef.current < 400;
		lastOvalClickTimeRef.current = now;

		if (!isDoubleClick) return;

		displayCapturedPhoto(debugFace);
	}, [displayCapturedPhoto]);

	const retakePhoto = () => {
		trackPlausibleEvent(isShadematch ? 'sm_photo_retake_clicked' : 'sd_photo_retake_clicked');
		dispatch({ type: actionTypes.REMOVE_ANALYSIS_WARNINGS });
		setIsPhotoCaptured(false);
		setSkinDiagLoading(false);
		setSkinDiagValidated(false);
		displayWaves(true);
		setDetectionStatus({ loading: true });
		setCountdownValue(null);
	};

	const usePhoto = () => {
		trackPlausibleEvent(isShadematch ? 'sm_photo_accepted' : 'sd_photo_accepted');
		dispatch({ type: actionTypes.SWITCH_STEP, value: isShadematch ? StepView.shadematchWristCamera : StepView.quests });

		if (!isShadematch) {
			sendAnalytics('photo ready confirmation', 'start scan', sessionID);
		}
	};

	return (
		<Component>
			{!isPhotoCaptured && (
				<>
					<StyledA3Camera
						ref={cameraVideoRef}
						detectionInterval={100}
						countdown={3000}
						videoConstraints={VIDEO_CONSTRAINTS}
						detectionConstraints={DETECTION_CONSTRAINTS_CENTER}
						photoSettings={PHOTO_SETTINGS}
						onPhotoTaken={handlePhotoTaken}
						onStatusChange={handleStatusChange}
						onCountdownStart={startCountdown}
						onCountdownStop={stopCountdown}
						onError={handleCameraError}
						customConstraintHandler={isShadematch ? handleLightConstraint : undefined}
						warnings={WARNINGS}
						useGrabFrame
					/>
					{isShadematch && SHOW_LIGHT_MODEL_PREVIEW && (
						<LightModelPreview
							ref={lightModelPreviewRef}
							$visible={lightModelResult !== null}
							role="img"
							aria-label="Light classification model input"
						/>
					)}
					<CenteredOvalContainer $isDisplayed={!setEyeLevelValidation(detectionStatus)}>
						<EyeText $isGood={detectionStatus.good}>{t('eye-level')}</EyeText>
						{countdownValue !== null && <CountDownNumberBis>{countdownValue}</CountDownNumberBis>}
						<Oval $isGood={detectionStatus.good} src={window.innerWidth < 391 ? EnlargedOvalImg : OvalImg} onClick={handleDebugOvalClick} />
					</CenteredOvalContainer>

					<AnimatePresence>
						{detectionStatus.loading !== true && !isCameraReady && (
							<CameraPanel initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
								<WarningContainer>
									<CameraIcon isValidate={setEyeLevelValidation(detectionStatus)} src={setEyeLevelValidation(detectionStatus) ? okIcon : badIcon} />
									<CameraWarningText isValidate={setEyeLevelValidation(detectionStatus)}>{t('adjust-eye-level', 'Adjust eye level')}</CameraWarningText>
								</WarningContainer>

								<WarningContainer>
									<CameraIcon isValidate={setDistanceValidation(detectionStatus)} src={setDistanceValidation(detectionStatus) ? okIcon : badIcon} />
									<CameraWarningText isValidate={setDistanceValidation(detectionStatus)}>{setDistanceText(t, detectionStatus)}</CameraWarningText>
								</WarningContainer>

								<WarningContainer>
									<CameraIcon isValidate={setLightningValidation(detectionStatus) && isLightReady} src={setLightningValidation(detectionStatus) && isLightReady ? okIcon : badIcon} />
									<CameraWarningText isValidate={setLightningValidation(detectionStatus) && isLightReady}>{getLightingText(t)}</CameraWarningText>
								</WarningContainer>
							</CameraPanel>
						)}
					</AnimatePresence>
				</>
			)}

			<AnimatePresence>{isPhotoCaptured && userImg && <UserImg initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} src={userImg} />}</AnimatePresence>

			<BottomContainer>
				{!isShadematch && !storeErrors.includes(AnalysisError.no_error) && storeErrors.length > 0 && (
					<WarningErrorBackground>
						<WarningErrorContainer>{translateError(t, storeErrors[0])}</WarningErrorContainer>
					</WarningErrorBackground>
				)}

				<AnimatePresence>
					{isWavesDisplayed && !isCameraReady && (
						<WaveContainer isDisplayed={true} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
							<Wave initial={{ width: '32px', height: '32px', opacity: 0 }} animate={{ width: '120px', height: '120px', opacity: [0, 0.5, 0] }} exit={{ opacity: 0 }} transition={{ repeat: !isCameraReady ? Infinity : 0, duration: 3, times: [0, 0.5, 0.8], type: 'tween' }} />
							<Wave initial={{ width: '32px', height: '32px', opacity: 0 }} animate={{ width: '120px', height: '120px', opacity: [0, 0.5, 0] }} transition={{ repeat: !isCameraReady ? Infinity : 0, duration: 3, times: [0, 0.5, 0.8], type: 'tween', delay: 1 }} />
							<Wave initial={{ width: '32px', height: '32px', opacity: 0 }} animate={{ width: '120px', height: '120px', opacity: [0, 0.5, 0] }} transition={{ repeat: !isCameraReady ? Infinity : 0, duration: 3, times: [0, 0.5, 0.8], type: 'tween', delay: 2 }} />
						</WaveContainer>
					)}
				</AnimatePresence>

				<AnimatePresence>{(!isPhotoCaptured || (!isShadematch && isSkinDiagLoading && storeErrors.length === 0)) && <WhiteText isDisplayed={true}>{isSkinDiagLoading ? t('loading') : setWhiteText(t, detectionStatus, lightModelResult, isShadematch)}</WhiteText>}</AnimatePresence>

				<AnimatePresence>
					{isPhotoCaptured && (isShadematch || isSkinDiagValidated) && (
						<CheckingContainer initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
							<CheckingVerticalCenter>
								<CheckingRoundButton onClick={retakePhoto}>
									<CheckingButton whileTap={{ scale: 1.05 }}>
										<CheckingIcon src={resetIcon} />
									</CheckingButton>
								</CheckingRoundButton>
								<CheckingText>{t('retake-photo', 'Retake photo')}</CheckingText>
							</CheckingVerticalCenter>

							<CheckingVerticalCenter>
								<CheckingRoundButton onClick={usePhoto}>
									<CheckingButton whileTap={{ scale: 1.05 }}>
										<CheckingIcon src={tickIcon} />
									</CheckingButton>
								</CheckingRoundButton>
								<CheckingText>{t('use-photo', 'Use photo')}</CheckingText>
							</CheckingVerticalCenter>
						</CheckingContainer>
					)}
				</AnimatePresence>
			</BottomContainer>

			<AnimatePresence>{isFlashing && <Flash initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1 }} />}</AnimatePresence>
		</Component>
	);
};

const setWhiteText = (t: Function, status: FaceLandmarksDetectionStatus, lightModelResult: LightModelResult | null, useLightModel: boolean): string => {
	if (status.loading) {
		return t('loading');
	}

	if (status.good) {
		if (!useLightModel || isLightModelResultOk(lightModelResult)) {
			return t('hold-still', 'Hold still');
		}

		if (lightModelResult?.topPrediction.label === 'uneven') {
			return 'Uneven lighting detected';
		}

		if (lightModelResult?.topPrediction.label === 'too_dark') {
			return 'Dark environment detected';
		}

		return getLightingText(t);
	}

	return t('finding-your-face', 'Finding your face...');
};

const setEyeLevelValidation = (status: FaceLandmarksDetectionStatus): boolean => {
	if (status.loading || status.noFace) {
		return false;
	}

	return !status.badOrientation && !status.badPositionBottom && !status.badPositionLeft && !status.badPositionRight && !status.badPositionTop;
};

const setDistanceValidation = (status: FaceLandmarksDetectionStatus): boolean => {
	if (status.loading || status.noFace) {
		return false;
	}
	return !status.badDistanceClose && !status.badDistanceFar;
};

const setDistanceText = (t: Function, status: FaceLandmarksDetectionStatus): string => {
	if (status.badDistanceClose) {
		return t('move-farther', 'move away');
	}

	if (status.badDistanceFar) {
		return t('move-closer', 'Move closer');
	}

	return t('move-closer', 'Move closer');
};

const setLightningValidation = (status: FaceLandmarksDetectionStatus): boolean => {
	if (status.loading) {
		return false;
	}

	return !status.badBrightness;
};

const getLightingText = (t: Function): string => t('adjust-lighting', 'Adjust lighting');

const Component = styled.div`
	position: fixed;
	inset: 0;
	background: #000;
	overflow: hidden;
`;

const StyledA3Camera = styled(A3Camera)`
	width: 100vw;
	height: 100vh;
	object-fit: cover;
`;

const LightModelPreview = styled.canvas<{ $visible: boolean }>`
	position: absolute;
	left: max(12px, env(safe-area-inset-left));
	bottom: max(12px, env(safe-area-inset-bottom));
	width: clamp(80px, 24vw, 128px);
	height: auto;
	border: 1px solid white;
	border-radius: 6px;
	z-index: 11;
	pointer-events: none;
	visibility: ${props => props.$visible ? 'visible' : 'hidden'};
`;

const CenteredOvalContainer = styled(OvalContainer)`
	position: fixed;
	top: 50%;
	left: 0;
	transform: translateY(-50%);
`;

export default NewCamera;
